// src/utils/htmlReceiptGenerator.js
// Thermal receipt generator - fully dynamic, no hardcoded values

import { formatUTCDate } from './dateUtils'
import { DEFAULT_CUSTOMERS } from '../config/constants'

export class HTMLReceiptGenerator {
  /**
   * Generate an HTML receipt that can be printed on a thermal printer
   * @param {Object} sale - Sale / order data
   * @param {Object} options - Options for receipt generation
   *   branchInfo: { name, address, phoneNumber, email }
   *   serviceFeeRate: number (e.g. 10 for 10%)
   *   cashierName: string
   *   logoBase64: string (data URL or base64 string)
   * @returns {string} HTML content
   */
  static generateReceiptHTML(sale, options = {}) {
    const {
      branchInfo = {},
      serviceFeeRate = null,  // null = no service charge shown unless in fees
      cashierName = '',
      logoBase64 = null,
    } = options

    const companyName  = branchInfo.name        || 'TEZLAA CAFÉ'
    const companyAddress = branchInfo.address   || ''
    const companyPhone = branchInfo.phoneNumber || branchInfo.phone || ''
    const companyEmail = branchInfo.email       || ''

    console.log('🧾 generateReceiptHTML called with sale:', sale)
    console.log('🧾 Options:', { branchInfo, serviceFeeRate, cashierName })

    // --------------- Items ---------------
    const items = sale.orderItems || sale.items || sale.orderItems || []
    console.log('🧾 Items found:', items.length, items)

    // --------------- Totals ---------------
    // Try orderPayment first (new API structure), then fallback
    const subtotal = sale.orderPayment?.subtotal
      || sale.subtotal
      || items.reduce((sum, item) => {
           const price = item.unitPrice || item.price || item.product?.outletPrice || 0
           const qty   = item.quantity  || item.qty  || 1
           return sum + price * qty
         }, 0)

    // Discount: try all known explicit API paths first
    const orderLevelDiscount =
      sale.orderPayment?.discount                                          // completed order
      || sale.orderPromotionEngineResult?.orderDetails?.totalDiscount      // promotion engine result
      || sale.discount                                                      // top-level field
      || 0

    // Item-level discounts (product promotions applied per line)
    const itemLevelDiscount = (sale.orderItems || sale.items || []).reduce((sum, item) => {
      return sum + (item.discountAmount || item.discount || item.totalDiscount || 0)
    }, 0)

    // Use fees object from API if available
    const feeObj = sale.fees || {}
    const isDineIn = sale.orderType === 'dine_in' || (!!(sale.table && sale.table._id))

    // Preliminary: service charge from fees API (preferred, no discount needed)
    let serviceChargeAmount = 0
    let serviceChargeRate   = 0
    if (feeObj.serviceFeeAmount !== undefined) {
      serviceChargeAmount = feeObj.serviceFeeAmount
      serviceChargeRate   = feeObj.serviceFeeRate || serviceFeeRate || 0
    } else if (isDineIn && serviceFeeRate !== null && serviceFeeRate > 0) {
      // Estimate first — recalculated below once real discount is known
      serviceChargeAmount = subtotal * (serviceFeeRate / 100)
      serviceChargeRate   = serviceFeeRate
    }

    const packageFeeAmount = feeObj.packageFeeAmount !== undefined ? feeObj.packageFeeAmount : 0
    const packageFeeRate   = feeObj.packageFeeRate   || 0

    // Final total from API
    const apiTotal = sale.orderPayment?.totalAmount !== undefined 
      ? sale.orderPayment.totalAmount 
      : (sale.total !== undefined ? sale.total : 0)

    // Derived discount: backend applied discount but didn't send explicit field.
    // Formula: subtotal + fees - apiTotal  → e.g. 650 + 58.50 - 643.50 = 65.00
    const rawDerived = (apiTotal > 0 && subtotal > 0)
      ? Math.round((subtotal + (feeObj.totalFees !== undefined ? feeObj.totalFees : (serviceChargeAmount + packageFeeAmount)) - apiTotal) * 100) / 100
      : 0
    const derivedDiscount = rawDerived > 0.009 ? rawDerived : 0

    // Final discount = highest across all sources
    const promotionDiscount = Math.max(orderLevelDiscount, itemLevelDiscount, derivedDiscount)

    // Recalculate service charge on correct post-discount base (only if we estimated it above)
    if (promotionDiscount > 0 && feeObj.serviceFeeAmount === undefined && isDineIn && serviceFeeRate !== null && serviceFeeRate > 0) {
      serviceChargeAmount = (subtotal - promotionDiscount) * (serviceFeeRate / 100)
    }

    const totalFees = feeObj.totalFees !== undefined ? feeObj.totalFees : (serviceChargeAmount + packageFeeAmount)
    const calculatedTotal = subtotal - promotionDiscount + totalFees
    const hasApiTotal = (sale.orderPayment && typeof sale.orderPayment.totalAmount === 'number') || typeof sale.total === 'number';
    const finalTotal = hasApiTotal ? apiTotal : calculatedTotal

    // Paid & change
    const paidAmount      = sale.orderPayment?.paidAmount      || 0
    const remainingAmount = sale.orderPayment?.remainingAmount || 0
    const paymentStatus   = sale.paymentStatus || (sale.orderPayment ? 'paid' : '')

    // Payment methods
    const paymentMethods = sale.orderPayment?.paymentMethods || []
    const cashMethod   = paymentMethods.find(m => m.method === 'cash')
    const cardMethods  = paymentMethods.filter(m => m.method === 'card')
    const redeemMethod = paymentMethods.find(m => m.method === 'redeem')

    const cashPaid         = cashMethod?.cashDetails?.receivedAmount || cashMethod?.amount || 0
    const cashBillAmount   = cashMethod?.amount || 0
    const changeAmount     = cashMethod?.cashDetails?.changeAmount   || Math.max(0, cashPaid - cashBillAmount)
    const redeemedPoints   = redeemMethod?.redeemDetails?.pointsUsed || redeemMethod?.amount || 0

    // Customer
    const WALKING_CUSTOMER_ID = DEFAULT_CUSTOMERS.WALKING_CUSTOMER
    const customer       = sale.customer || null
    const customerId     = customer?._id || customer?.id || sale.customer || ''
    const isWalkingCustomer = String(customerId) === WALKING_CUSTOMER_ID || !customerId
    const customerName   = (!isWalkingCustomer && (customer?.name || sale.customerName)) || ''
    const customerPhone  = (!isWalkingCustomer && (customer?.mobileNumber || customer?.phone || sale.customerMobile)) || ''
    const pointsBalance  = (!isWalkingCustomer && customer?.pointsBalance !== undefined) ? customer.pointsBalance : null

    // Order info
    const orderNumber  = sale.orderNumber || 'N/A'
    // Try all possible date field names from the API
    const orderDate    = sale.orderDate || sale.createdAt || sale.updatedAt || sale.date || null
    const orderType    = sale.orderTypeConfig?.label
      || (isDineIn ? 'Dine In'
        : sale.orderType === 'take_away' ? 'Take Away'
        : sale.orderType === 'takeaway'  ? 'Take Away'
        : sale.orderType === 'delivery'  ? 'Delivery'
        : sale.orderType?.replace(/_/g, ' ') || 'N/A')
    const tableInfo    = sale.table?.name || sale.table?.number || ''

    // Logo HTML
    const logoHTML = logoBase64
      ? `<div class="logo-wrap"><img class="logo" src="${logoBase64}" alt="Logo"/></div>`
      : ''

    console.log('🧾 Totals:', { subtotal, promotionDiscount, orderLevelDiscount, itemLevelDiscount, derivedDiscount, serviceChargeAmount, packageFeeAmount, finalTotal })

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Receipt - ${orderNumber}</title>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        body {
            font-family: 'Courier Prime', 'Courier New', monospace;
            width: 302px;          /* ~80mm */
            max-width: 302px;
            margin: 0 auto;
            padding: 12px 10px;
            background: #fff;
            color: #000;
            font-size: 14px;
            line-height: 1.55;
        }

        /* ===== HEADER ===== */
        .header {
            text-align: center;
            padding-bottom: 10px;
            border-bottom: 2px dashed #000;
            margin-bottom: 10px;
        }
        .logo-wrap {
            margin-bottom: 6px;
        }
        .logo {
            width: 90px;
            height: auto;
            object-fit: contain;
        }
        .company-name {
            font-size: 16px;
            font-weight: 700;
            letter-spacing: 2px;
            text-transform: uppercase;
            margin-bottom: 3px;
        }
        .company-info {
            font-size: 12px;
            line-height: 1.6;
            color: #000;
        }
        .receipt-title {
            font-size: 14px;
            font-weight: 700;
            margin-top: 8px;
            letter-spacing: 3px;
            text-align: center;
        }

        /* ===== ORDER INFO ===== */
        .section {
            margin-bottom: 8px;
        }
        .separator {
            border: none;
            border-top: 1px dashed #000;
            margin: 8px 0;
        }
        .separator-solid {
            border: none;
            border-top: 1px solid #000;
            margin: 6px 0;
        }
        .info-row {
            display: flex;
            justify-content: space-between;
            font-size: 13px;
            margin-bottom: 3px;
        }
        .info-label { color: #000; font-weight: 600; white-space: nowrap; margin-right: 4px; }
        .info-value { text-align: right; word-break: break-word; max-width: 175px; }

        /* ===== CUSTOMER ===== */
        .customer-section {
            background: #f5f5f5;
            padding: 5px 6px;
            border-radius: 3px;
            margin-bottom: 8px;
        }
        .customer-title {
            font-weight: 700;
            font-size: 13px;
            margin-bottom: 3px;
            text-transform: uppercase;
            letter-spacing: 1px;
        }

        /* ===== ITEMS ===== */
        .items-header {
            display: flex;
            justify-content: space-between;
            font-weight: 700;
            font-size: 13px;
            padding: 3px 0;
            border-bottom: 1px solid #000;
            margin-bottom: 4px;
        }
        .item-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            font-size: 13px;
            margin-bottom: 4px;
            gap: 4px;
        }
        .item-name {
            flex: 1;
            word-break: break-word;
            padding-right: 4px;
        }
        .item-name .item-promo {
            font-size: 9px;
            color: #444;
            display: block;
        }
        .item-qty-price {
            text-align: right;
            white-space: nowrap;
            min-width: 90px;
        }

        /* ===== TOTALS ===== */
        .totals-section {
            padding-top: 6px;
        }
        .total-row {
            display: flex;
            justify-content: space-between;
            font-size: 13px;
            margin-bottom: 3px;
        }
        .total-row.discount { color: #000; }
        .total-row.service  { color: #000; }
        .total-row.final {
            font-size: 15px;
            font-weight: 700;
            padding-top: 5px;
            border-top: 2px solid #000;
            margin-top: 4px;
        }

        /* ===== PAYMENT ===== */
        .payment-section {
            padding-top: 6px;
            font-size: 13px;
        }
        .payment-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 3px;
        }
        .payment-row.highlight {
            font-weight: 700;
        }
        .payment-row.paid-ok {
            color: #000;
            font-weight: 700;
        }
        .payment-row.balance {
            color: #000;
            font-weight: 700;
        }

        /* ===== POINTS / REDEMPTION ===== */
        .points-section {
            background: #f0f0f0;
            padding: 5px 6px;
            border-radius: 3px;
            margin-top: 6px;
            font-size: 13px;
        }
        .points-title {
            font-weight: 700;
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 3px;
        }

        /* ===== FOOTER ===== */
        .footer {
            text-align: center;
            padding-top: 10px;
            font-size: 12px;
            color: #000;
        }
        .footer .thank-you {
            font-size: 14px;
            font-weight: 700;
            color: #000;
            margin-bottom: 4px;
        }
        .footer .note {
            font-size: 11px;
            color: #000;
            margin-top: 4px;
        }

        /* ===== PRINT ===== */
        @media print {
            body {
                width: 100%;
                max-width: none;
                padding: 5px;
            }
            @page {
                size: 80mm auto;
                margin: 0;
            }
        }
    </style>
</head>
<body>

    <!-- HEADER -->
    <div class="header">
        ${logoHTML}
        <div class="company-name">${companyName}</div>
        <div class="company-info">
            ${companyAddress ? `<div>${companyAddress}</div>` : ''}
            ${companyPhone  ? `<div>Tel: ${companyPhone}</div>` : ''}
            ${companyEmail  ? `<div>${companyEmail}</div>`  : ''}
        </div>
        <div class="receipt-title">*** RECEIPT ***</div>
    </div>

    <!-- ORDER INFO -->
    <div class="section">
        <div class="info-row">
            <span class="info-label">Order #:</span>
            <span class="info-value">${orderNumber}</span>
        </div>
        <div class="info-row">
            <span class="info-label">Date:</span>
            <span class="info-value">${this.formatDate(orderDate)}</span>
        </div>
        <div class="info-row">
            <span class="info-label">Type:</span>
            <span class="info-value">${orderType}</span>
        </div>
        ${tableInfo ? `
        <div class="info-row">
            <span class="info-label">Table:</span>
            <span class="info-value">${tableInfo}</span>
        </div>` : ''}
        ${cashierName ? `
        <div class="info-row">
            <span class="info-label">Cashier:</span>
            <span class="info-value">${cashierName}</span>
        </div>` : ''}
    </div>

    <!-- CUSTOMER INFO -->
    ${customerName ? `
    <div class="customer-section">
        <div class="customer-title">Customer</div>
        <div class="info-row">
            <span class="info-label">Name:</span>
            <span class="info-value">${customerName}</span>
        </div>
        ${customerPhone ? `
        <div class="info-row">
            <span class="info-label">Phone:</span>
            <span class="info-value">${customerPhone}</span>
        </div>` : ''}
    </div>` : ''}

    <hr class="separator"/>

    <!-- ITEMS -->
    <div class="section">
        <div class="items-header">
            <span>Item</span>
            <span>Qty x Price</span>
        </div>
        ${items.map((item, index) => {
          const name    = item.product?.name
                       || item.name
                       || item.productName
                       || item.itemName
                       || `Item #${index + 1}`
          const qty     = item.quantity || item.qty || 1
          const price   = item.unitPrice || item.price || item.product?.outletPrice || 0
          const lineTotal = (price * qty).toFixed(2)
          const hasPromo  = item.promotionEngineResult?.hasPromotion
          const promoName = item.promotionEngineResult?.promotion?.name || ''

          return `
            <div class="item-row">
                <span class="item-name">
                    ${name}
                    ${hasPromo ? `<span class="item-promo">🎉 ${promoName}</span>` : ''}
                </span>
                <span class="item-qty-price">${qty} x ${this.formatCurrency(price)}<br/>= ${this.formatCurrency(parseFloat(lineTotal))}</span>
            </div>`
        }).join('')}
    </div>

    <hr class="separator-solid"/>

    <!-- TOTALS -->
    <div class="totals-section">
        <div class="total-row">
            <span>Subtotal</span>
            <span>${this.formatCurrency(subtotal)}</span>
        </div>
        ${promotionDiscount > 0 ? `
        <div class="total-row discount">
            <span>Discount</span>
            <span>- ${this.formatCurrency(promotionDiscount)}</span>
        </div>` : ''}
        ${serviceChargeAmount > 0 ? `
        <div class="total-row service">
            <span>Service Charge (${serviceChargeRate}%)</span>
            <span>${this.formatCurrency(serviceChargeAmount)}</span>
        </div>` : ''}
        ${packageFeeAmount > 0 ? `
        <div class="total-row service">
            <span>Package Fee${packageFeeRate > 0 ? ` (${packageFeeRate}%)` : ''}</span>
            <span>${this.formatCurrency(packageFeeAmount)}</span>
        </div>` : ''}
        <div class="total-row final">
            <span>TOTAL</span>
            <span>${this.formatCurrency(finalTotal)}</span>
        </div>
    </div>

    <hr class="separator"/>

    <!-- PAYMENT -->
    <div class="payment-section">
        ${cashPaid > 0 ? `
        <div class="payment-row">
            <span>Cash Received</span>
            <span>${this.formatCurrency(cashPaid)}</span>
        </div>
        ${changeAmount > 0 ? `
        <div class="payment-row highlight">
            <span>Change</span>
            <span>${this.formatCurrency(changeAmount)}</span>
        </div>` : ''}` : ''}

        ${cardMethods.map(c => `
        <div class="payment-row">
            <span>Card${c.cardDetails?.lastFourDigits ? ` (*${c.cardDetails.lastFourDigits})` : ''}</span>
            <span>${this.formatCurrency(c.amount || 0)}</span>
        </div>`).join('')}

        ${redeemMethod ? `
        <div class="payment-row">
            <span>Points Redeemed</span>
            <span>${this.formatCurrency(redeemedPoints)}</span>
        </div>` : ''}

        ${paidAmount > 0 ? `
        <div class="payment-row">
            <span>Total Paid</span>
            <span>${this.formatCurrency(paidAmount)}</span>
        </div>` : ''}

        ${remainingAmount > 0 ? `
        <div class="payment-row balance">
            <span>Balance Due</span>
            <span>${this.formatCurrency(remainingAmount)}</span>
        </div>` : ''}

        ${paymentStatus === 'paid' && remainingAmount === 0 ? `
        <div class="payment-row paid-ok">
            <span>✓ PAYMENT COMPLETE</span>
        </div>` : ''}
    </div>

    <!-- POINTS / REDEMPTION SUMMARY (hidden for walking/anonymous customers) -->
    ${(!isWalkingCustomer && (redeemedPoints > 0 || pointsBalance !== null)) ? `
    <div class="points-section">
        <div class="points-title">Loyalty Points</div>
        ${redeemedPoints > 0 ? `
        <div class="info-row">
            <span class="info-label">Points Used:</span>
            <span class="info-value">${redeemedPoints.toFixed(2)} pts</span>
        </div>` : ''}
        ${pointsBalance !== null ? `
        <div class="info-row">
            <span class="info-label">Points Balance:</span>
            <span class="info-value">${Number(pointsBalance).toFixed(2)} pts</span>
        </div>` : ''}
    </div>` : ''}

    <!-- FOOTER -->
    <hr class="separator"/>
    <div class="footer">
        <div class="thank-you">Thank you for your visit!</div>
        <div>Please come again</div>
        <div class="note">This is a computer-generated receipt.</div>
        <div class="note">Printed: ${new Date().toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
    </div>

</body>
</html>`.trim()
  }

  /**
   * Format date for display — uses multiple fallback strategies
   */
  static formatDate(date) {
    if (!date) return 'N/A'
    try {
      const result = formatUTCDate(date, 'dd/MM/yyyy HH:mm')
      // formatUTCDate might return empty string on bad input
      if (result && result.trim()) return result
    } catch { /* fall through */ }
    try {
      const d = new Date(date)
      if (!isNaN(d.getTime())) {
        return d.toLocaleString('en-GB', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit'
        })
      }
    } catch { /* fall through */ }
    return 'N/A'
  }

  /**
   * Format currency value
   */
  static formatCurrency(amount) {
    if (typeof amount !== 'number' || isNaN(amount)) return 'Rs.0.00'
    return `Rs.${amount.toFixed(2)}`
  }

  /**
   * Print receipt using hidden iframe (same tab) — accepts extra async options (logoBase64 etc)
   * @param {Object} sale - Sale data
   * @param {Object} options - Receipt options
   */
  static printReceipt(sale, options = {}) {
    const htmlContent = this.generateReceiptHTML(sale, options)

    // Create hidden iframe for printing
    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.right    = '0'
    iframe.style.bottom   = '0'
    iframe.style.width    = '0'
    iframe.style.height   = '0'
    iframe.style.border   = 'none'

    document.body.appendChild(iframe)

    const iframeDoc = iframe.contentWindow.document
    iframeDoc.open()
    iframeDoc.write(htmlContent)
    iframeDoc.close()

    setTimeout(() => {
      iframe.contentWindow.focus()
      iframe.contentWindow.print()
      setTimeout(() => {
        document.body.removeChild(iframe)
      }, 1500)
    }, 350)
  }

  /**
   * Download receipt as HTML file
   */
  static downloadHTML(sale, filename = null, options = {}) {
    const htmlContent = this.generateReceiptHTML(sale, options)
    const blob = new Blob([htmlContent], { type: 'text/html' })

    const url  = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href     = url
    link.download = filename || `receipt_${sale.orderNumber || Date.now()}.html`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
}